import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import Card, { CardHeader, CardContent } from "@/components/shared/ui/Card";
import { ExternalLink, Newspaper, FileText } from "lucide-react";

interface PressHighlight {
  id: string;
  publication_name: string;
  title: string;
  url?: string;
  pdf_url?: string;
  publication_date?: string;
  description?: string;
  display_order: number;
}

interface PressWidgetProps {
  projectId: string;
}

export default function PressWidget({ projectId }: PressWidgetProps) {
  const [highlights, setHighlights] = useState<PressHighlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPressHighlights();
  }, [projectId]);

  const fetchPressHighlights = async () => {
    try {
      setLoading(true);

      const { data, error: fetchError } = await supabase
        .from("press_highlights")
        .select(
          "id, publication_name, title, url, pdf_url, publication_date, description, display_order"
        )
        .eq("project_id", projectId)
        .order("display_order", { ascending: true });

      if (fetchError) throw fetchError;

      setHighlights(data || []);
    } catch (err) {
      console.error("Error fetching press highlights:", err);
      setError("Failed to load press highlights");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <Card variant="bordered">
        <CardHeader>
          <h3 className="text-lg sm:text-xl font-bold text-black">
            Press Highlights
          </h3>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 sm:py-8 text-gray-500 text-sm sm:text-base">
            Loading press coverage...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || highlights.length === 0) {
    return null;
  }

  return (
    <Card variant="bordered">
      <CardHeader color="orange">
        <h3 className="text-lg sm:text-xl font-bold text-black">
          Press Highlights
        </h3>
        <p className="text-xs sm:text-sm text-gray-600 mt-1">
          Media coverage and press mentions
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 sm:space-y-4 max-h-[400px] overflow-y-auto">
          {highlights.map((highlight) => (
            <div
              key={highlight.id}
              className="border-2 border-black rounded-lg p-3 sm:p-4 bg-white"
            >
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-r from-orange-400 to-red-600 flex items-center justify-center">
                  <Newspaper className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* Publication & Date */}
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs sm:text-sm font-bold text-black">
                      {highlight.publication_name}
                    </span>
                    {highlight.publication_date && (
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        {formatDate(highlight.publication_date)}
                      </span>
                    )}
                  </div>

                  {/* Headline */}
                  <h4 className="text-sm sm:text-base font-bold text-black break-words mb-2">
                    {highlight.title}
                  </h4>

                  {/* Description */}
                  {highlight.description && (
                    <div className="text-xs sm:text-sm text-gray-700 italic border-l-4 border-orange-400 pl-3 py-1 mb-3">
                      "{highlight.description}"
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {highlight.url && (
                      <a
                        href={highlight.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500 text-white text-xs font-bold rounded border-2 border-black hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View Article
                      </a>
                    )}
                    {highlight.pdf_url && (
                      <a
                        href={highlight.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-500 text-white text-xs font-bold rounded border-2 border-black hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
                      >
                        <FileText className="w-3 h-3" />
                        View PDF
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
